import { useQuery } from "@tanstack/react-query";

/** Current place + weather, derived from the browser's geolocation. */
export interface WeatherInfo {
  place: string;
  country?: string;
  latitude: number;
  longitude: number;
  temperature: number;
  apparentTemperature: number;
  windSpeed: number;
  weatherCode: number;
  isDay: boolean;
}

const COORDS_KEY = "weather.lastCoords";

interface Coords {
  latitude: number;
  longitude: number;
}

/** Resolve the user's coordinates, caching the last fix so we don't re-prompt. */
function getCoords(): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      const cached = readCachedCoords();
      return cached ? resolve(cached) : reject(new Error("Geolocation isn't available"));
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        try {
          localStorage.setItem(COORDS_KEY, JSON.stringify(coords));
        } catch {
          /* private mode — ignore */
        }
        resolve(coords);
      },
      (err) => {
        // Denied/unavailable → fall back to the last known fix if we have one.
        const cached = readCachedCoords();
        if (cached) return resolve(cached);
        reject(new Error(err.message || "Location permission denied"));
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30 * 60_000 },
    );
  });
}

function readCachedCoords(): Coords | null {
  try {
    const raw = localStorage.getItem(COORDS_KEY);
    return raw ? (JSON.parse(raw) as Coords) : null;
  } catch {
    return null;
  }
}

async function fetchWeather(): Promise<WeatherInfo> {
  const { latitude, longitude } = await getCoords();

  const weatherUrl =
    `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
    `&current=temperature_2m,apparent_temperature,weather_code,is_day,wind_speed_10m&timezone=auto`;
  const geoUrl =
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}` +
    `&longitude=${longitude}&localityLanguage=en`;

  const [weatherRes, geoRes] = await Promise.all([fetch(weatherUrl), fetch(geoUrl)]);
  if (!weatherRes.ok) throw new Error("Couldn't load weather");
  const weather = (await weatherRes.json()) as {
    current: {
      temperature_2m: number;
      apparent_temperature: number;
      weather_code: number;
      is_day: number;
      wind_speed_10m: number;
    };
  };

  let place = "Your location";
  let country: string | undefined;
  if (geoRes.ok) {
    const geo = (await geoRes.json()) as {
      city?: string;
      locality?: string;
      principalSubdivision?: string;
      countryName?: string;
    };
    place = geo.city || geo.locality || geo.principalSubdivision || place;
    country = geo.countryName;
  }

  const c = weather.current;
  return {
    place,
    country,
    latitude,
    longitude,
    temperature: c.temperature_2m,
    apparentTemperature: c.apparent_temperature,
    windSpeed: c.wind_speed_10m,
    weatherCode: c.weather_code,
    isDay: c.is_day === 1,
  };
}

export function useWeather() {
  return useQuery({
    queryKey: ["weather"],
    queryFn: fetchWeather,
    staleTime: 15 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
